import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createAsk,
  deleteConversation,
  getAsk,
  getThread,
  listConversations,
  sendAskFeedback,
} from '../../api/ask';
import { useEnvironment } from '../useEnvironment/useEnvironment';
import type { IAskFeedbackInput, IAskInput, IAskRequest } from '../../types';

const POLL_MS = 2000;

export const isAskSettled = (request: IAskRequest | undefined): boolean =>
  request?.status === 'done' || request?.status === 'failed';

/**
 * One request, polled every two seconds until it is done or failed. `initial` seeds the cache (a history row or
 * the record returned by the ask mutation) so settled requests never fetch at all.
 */
export const useAskRequest = (initial: IAskRequest) => {
  const { environment } = useEnvironment();
  const query = useQuery({
    queryKey: ['ask', environment, initial.id],
    queryFn: () => getAsk(environment, initial.id),
    initialData: { data: initial },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    select: ({ data }) => data,
  });
  const settled = isAskSettled(query.data);
  const { refetch } = query;
  useEffect(() => {
    if (settled) {
      return undefined;
    }
    const timer = setInterval(() => {
      refetch();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [settled, refetch]);
  return query;
};

export const useConversations = (tenant: string | null) => {
  const { environment } = useEnvironment();
  return useQuery({
    queryKey: ['ask-conversations', environment, tenant],
    queryFn: () => listConversations(environment, tenant as string),
    enabled: Boolean(tenant),
    select: ({ data }) => data,
    staleTime: 10_000,
  });
};

/** Every request in one chat. Only fetched for chats that exist on the server (`exists`), not for a fresh draft. */
export const useThread = (id: string | null, exists: boolean) => {
  const { environment } = useEnvironment();
  return useQuery({
    queryKey: ['ask-thread', environment, id],
    queryFn: () => getThread(environment, id as string),
    enabled: Boolean(id) && exists,
    select: ({ data }) => data,
    staleTime: 10_000,
  });
};

export const useAskMutations = () => {
  const { environment } = useEnvironment();
  const client = useQueryClient();
  const remember = ({ data }: { data: IAskRequest }) => {
    client.setQueryData(['ask', environment, data.id], { data });
    client.invalidateQueries({ queryKey: ['ask-conversations', environment, data.tenant] });
  };
  const removeConversation = useMutation({
    mutationFn: (id: string) => deleteConversation(environment, id),
    onSuccess: (_result, id) => {
      client.removeQueries({ queryKey: ['ask-thread', environment, id] });
      client.invalidateQueries({ queryKey: ['ask-conversations', environment] });
    },
  });
  const ask = useMutation({
    mutationFn: (input: IAskInput) => createAsk(environment, input),
    onSuccess: remember,
  });
  const feedback = useMutation({
    mutationFn: ({ id, input }: { id: string; input: IAskFeedbackInput }) =>
      sendAskFeedback(environment, id, input),
    onSuccess: remember,
  });
  return { ask, feedback, removeConversation };
};
